import { useMemo, useState } from 'react';
import { Users, Search, Calendar } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import type { TeamMember } from '../types';

interface TeamAttendanceProps {
  team: TeamMember[];
  loading: boolean;
  isError: boolean;
  selectedDept?: string;
  onSelectDept?: (dept: string) => void;
  selectedDate?: string;
  onSelectDate?: (date: string) => void;
}

function memberState(m: TeamMember): 'present' | 'checked_out' | 'absent' {
  if (m.check_out) return 'checked_out';
  if (m.check_in) return 'present';
  return 'absent';
}

export function TeamAttendance({
  team,
  loading,
  isError,
  selectedDept = 'all',
  onSelectDept,
  selectedDate,
  onSelectDate,
}: TeamAttendanceProps) {
  const [search, setSearch] = useState('');

  const kpis = useMemo(() => {
    let present = 0;
    let checkedOut = 0;
    let notIn = 0;
    for (const m of team) {
      const s = memberState(m);
      if (s === 'present') present++;
      else if (s === 'checked_out') checkedOut++;
      else notIn++;
    }
    return { present, checkedOut, notIn, total: team.length };
  }, [team]);

  const filteredTeam = useMemo(() => {
    if (!search.trim()) return team;
    const q = search.trim().toLowerCase();
    return team.filter((m) =>
      (m.display_name || '').toLowerCase().includes(q) ||
      (m.department || '').toLowerCase().includes(q)
    );
  }, [team, search]);

  const columns: Column<TeamMember>[] = [
    {
      key: 'name',
      header: 'Employee',
      render: (m) => (
        <div>
          <span className="font-semibold text-foreground text-xs">{m.display_name}</span>
          {m.department && (
            <span className="block text-[11px] text-muted-foreground capitalize">{m.department}</span>
          )}
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      render: (m) => (
        <span className="inline-block px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 capitalize">
          {m.department || 'General'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (m) => {
        const s = memberState(m);
        return (
          <Badge tone={s === 'present' ? 'success' : s === 'checked_out' ? 'info' : 'neutral'}>
            {s === 'present' ? 'Checked In' : s === 'checked_out' ? 'Checked Out' : 'Not Checked In'}
          </Badge>
        );
      },
    },
    { key: 'check_in', header: 'Check In', render: (m) => <span className="font-mono undefined-undefined font-medium text-xs">{m.check_in || '—'}</span> },
    { key: 'check_out', header: 'Check Out', render: (m) => <span className="font-mono undefined-undefined font-medium text-xs">{m.check_out || '—'}</span> },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      render: (m) => (m.hours != null && m.hours > 0 ? <span className="font-mono font-semibold text-xs">{m.hours}h</span> : '—'),
    },
  ];

  return (
    <Card className="rounded-xl border shadow-sm overflow-hidden">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-border bg-slate-50/50 dark:bg-slate-900/50 px-5 py-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <div>
            <h2 className="text-base font-bold text-foreground">
              Team Daily Attendance {selectedDate ? `(${selectedDate})` : '— Today'}
            </h2>
            <p className="text-xs text-muted-foreground">
              Real-time check-in and check-out status of employees across all departments
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          {onSelectDate && selectedDate && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="h-3.5 w-3.5 undefined-undefined" />
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => onSelectDate(e.target.value)}
                className="w-36 text-xs h-8"
              />
            </div>
          )}

          {onSelectDept && (
            <Select
              value={selectedDept}
              onChange={(e) => onSelectDept(e.target.value)}
              options={[
                { value: 'all', label: 'All Departments' },
                { value: 'sales', label: 'Sales' },
                { value: 'accounts', label: 'Accounts' },
                { value: 'production', label: 'Production / Dispatch' },
                { value: 'management', label: 'Management' },
                { value: 'hr', label: 'HR' },
              ]}
              className="w-40 text-xs h-8"
            />
          )}

          <div className="relative w-44">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search member..."
              className="w-full pl-7 text-xs h-8"
            />
            <Search className="h-3 w-3 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="space-y-4 p-5">
        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner />
          </div>
        ) : isError ? (
          <p className="text-xs undefined-undefined">Failed to load team attendance.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Kpi label="Total Roster" value={kpis.total} tone="neutral" />
              <Kpi label="Checked In (Active)" value={kpis.present} tone="success" />
              <Kpi label="Checked Out" value={kpis.checkedOut} tone="info" />
              <Kpi label="Not Checked In" value={kpis.notIn} tone="neutral" />
            </div>
            <Table
              columns={columns}
              data={filteredTeam}
              rowKey={(m) => m.id}
              empty="No team members found for the selected department or date."
            />
          </>
        )}
      </div>
    </Card>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone: 'success' | 'info' | 'neutral' }) {
  const toneCls =
    tone === 'success'
      ? 'undefined-undefined undefined-undefined'
      : tone === 'info'
        ? 'undefined-undefined undefined-undefined'
        : 'text-foreground';
  return (
    <div className="rounded-lg border border-border bg-slate-50/40 dark:bg-slate-900/40 px-4 py-3 text-center shadow-xs">
      <p className={`text-2xl font-bold ${toneCls}`}>{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}
